import { ImagePaths } from '../../lib/imageUrls.js';

export const purviewInADayExecutive = {

    id: 39,
    slug: 'purview-in-a-day-executive',
    ogImage: '/og/courses/purview-in-a-day-executive.jpg',
    courseCode: 'PURVIEW-EXEC-001',
    featured: true,
    title: 'Purview in a Day — Executive Edition',
    subtitle: 'Board-level Purview awareness for CXOs and decision-makers',
    valueProposition: 'Equip executives to sponsor a Microsoft Purview deployment with confidence — quantified risk reduction, vendor comparison, and a board-ready business case in a single day.',
    organization: 'EDUC4TE',
    duration: '8 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_3,
    shortDescription: 'PURVIEW-EXEC-001 — Purview executive briefing covering portfolio, ROI, vendor comparison, and board-level business case under Privacy Act 1988.',
    price: {
      aud: 1200,
      currency: 'AUD'
    },
    stripeConfig: {
    priceId: 'price_1TbdY7Ku1nyc322UeULpPEJv',
    productId: 'prod_UapCGaCm7cGdMB'
  },

    snapshot: {
      whoItsFor: [
        'CXOs (CEO, CFO, CTO, CISO, CPO) sponsoring a Microsoft Purview deployment',
        'Budget approvers and business sponsors evaluating Purview investment',
        'Board members with technology and compliance oversight responsibility',
        'Senior legal, compliance, and risk officers shaping data protection strategy'
      ],
      accreditation: 'Aligned to Microsoft Purview deployment guidance, Australian Privacy Act 1988, and APP 11 security of personal information obligations'
    },

    logistics: {
      classSize: 'Maximum 12 participants per cohort to support executive boardroom dialogue',
      requirements: [
        'No technical background required — designed for non-technical executives',
        'Computer or tablet with Microsoft Teams for virtual delivery',
        'Authority to sponsor or approve data protection investment',
        'Willingness to share organisational data protection context for tailored ROI modelling'
      ]
    },

    overview: 'Purview in a Day — Executive Edition is a strategic awareness programme for executives who need a board-level understanding of Microsoft Purview before committing to deployment. Across eight hours of facilitated boardroom dialogue you will work through the Purview portfolio, ROI modelling against your current regulatory exposure, a structured vendor comparison framework, and the business case narrative that boards, audit committees, and Australian regulators expect to see. The programme is grounded in Microsoft Purview deployment guidance, the Australian Privacy Act 1988, Australian Privacy Principle 11 (security of personal information), and the OAIC Notifiable Data Breaches scheme. You leave with a completed ROI model, a vendor comparison framework, and a draft deployment business case template ready to take into your next executive committee or board meeting. Designed for CXOs, budget approvers, and senior compliance leaders, the course is Module 1 of 6 in the EDUC4TE Purview Training Track — the natural entry point before committing your practitioners to operational Purview training.',

    outcomes: [
      'Understand the Microsoft Purview portfolio and articulate what each component protects',
      'Evaluate Purview return on investment against current data protection risk and regulatory exposure',
      'Apply a structured vendor comparison framework to assess Purview against alternative data protection platforms',
      'Identify organisational data protection gaps that Microsoft Purview is positioned to address',
      'Build an executive deployment business case with quantified risk reduction and Privacy Act 1988 alignment',
      'Articulate Purview value to boards, audit committees, and Australian regulators using outcome-focused language',
      'Sponsor a Purview deployment programme with clear sign-off criteria and governance checkpoints',
      'Communicate the link between Purview controls, APP 11 obligations, and Notifiable Data Breaches scheme readiness'
    ],

    audience: [
      'Chief executive, financial, technology, information security, and privacy officers',
      'Budget approvers and business sponsors for data protection investment',
      'Board members and audit committee members with technology oversight',
      'Senior legal, compliance, and risk officers',
      'Executive leaders preparing to commission Microsoft Purview deployment'
    ],

    prerequisites: [
      'Executive-level responsibility for data protection, compliance, or risk outcomes',
      'No technical or Microsoft Purview experience required',
      'Familiarity with your organisation\'s regulatory obligations is helpful but not required'
    ],

    modules: [
      {
        number: 1,
        title: 'Microsoft Purview portfolio — what each component protects',
        duration: '120 min',
        topics: [
          'Purview Information Protection, Data Loss Prevention, Data Lifecycle Management, Records Management, Insider Risk Management, Communication Compliance, eDiscovery, and Audit',
          'How the portfolio maps to data discovery, classification, protection, governance, and assurance',
          'Where Purview sits relative to Microsoft Defender XDR, Entra ID, and Microsoft 365 security',
          'Reference: Microsoft Learn — Microsoft Purview deployment guidance (updated 2025)'
        ]
      },
      {
        number: 2,
        title: 'ROI modelling and vendor comparison framework',
        duration: '150 min',
        topics: [
          'Quantifying current data protection risk and regulatory exposure (Privacy Act, APP 11, NDB scheme)',
          'Building a Purview ROI model that boards and CFOs find credible',
          'Vendor comparison framework — Purview against Symantec, Forcepoint, Netskope, and other Australian-market alternatives',
          'Decision criteria: portfolio breadth, Microsoft 365 integration, total cost of ownership, deployment risk',
          'Walkthrough: completing the Educ4te ROI Model and Vendor Comparison Framework on your own data'
        ]
      },
      {
        number: 3,
        title: 'Organisational data protection gap assessment',
        duration: '150 min',
        topics: [
          'Where personal information and regulated data live across your Microsoft 365 environment',
          'Gap analysis against APP 11 security obligations and Privacy Act 1988 record-keeping expectations',
          'Reading a Purview data discovery report and translating findings into executive language',
          'Prioritising gaps by regulator visibility, breach likelihood, and remediation effort',
          'Reference: OAIC — Australian Privacy Principles guidelines, Chapter 11 (Security of personal information)'
        ]
      },
      {
        number: 4,
        title: 'Executive business case and board communication',
        duration: '60 min',
        topics: [
          'Structuring the Purview deployment business case — problem, options, recommendation, financials, risks',
          'Quantified risk reduction language that audit committees and Australian regulators accept',
          'Communicating Purview value to boards, audit committees, and APRA-regulated supervisors',
          'Sign-off criteria, governance checkpoints, and the handover to practitioner training (Module 2 onwards)',
          'Take-home: draft Deployment Business Case template ready for next executive committee meeting'
        ]
      }
    ],

    format: {
      type: 'One-day executive briefing — instructor-led in-person boardroom or virtual via Microsoft Teams, or lunch-and-learn (8 x 1-hour sessions over 8 days)',
      includes: [
        '8 hours of facilitated boardroom dialogue and applied exercises',
        'Microsoft Purview ROI Model worksheet (completed during the course on your own data)',
        'Vendor Comparison Framework spreadsheet — Purview against Australian-market alternatives',
        'Deployment Business Case template — draft completed by end of day',
        'Privacy Act 1988 and APP 11 mapping reference pack',
        'Certificate of Completion suitable for CPD claims and board reporting',
        '30 days of email follow-up support to refine the business case before executive sign-off'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Available as a private on-site briefing for executive teams (up to 12), virtual instructor-led via Microsoft Teams, or condensed half-day format on request'
    },

    certification: 'Educ4te Certificate of Completion — Purview in a Day, Executive Edition. Suitable for CPD claims for directors, compliance officers, and senior risk professionals. Module 1 of 6 in the EDUC4TE Purview Training Track.',

    faqs: [
      {
        question: 'Do I need a technical background to attend?',
        answer: 'No. The course is purpose-built for non-technical executives — CXOs, budget approvers, board members, and senior legal or compliance officers. Every concept is presented in business and regulatory language, not Microsoft 365 console screenshots.'
      },
      {
        question: 'How is this different from the practitioner Purview courses?',
        answer: 'This Executive Edition is the decision-making layer — ROI, vendor comparison, business case, board communication. The practitioner courses (Purview for Copilot, Purview Retention at Scale) are 16-hour intensives covering hands-on configuration of DLP, retention, classifiers, and records management. Most organisations send executives to this course first, then send practitioners to the operational courses afterwards.'
      },
      {
        question: 'Will we cover Australian Privacy Act 1988 and APP 11 obligations?',
        answer: 'Yes. Privacy Act 1988, APP 11 (security of personal information), the OAIC Notifiable Data Breaches scheme, and APRA CPS 234 (where relevant) are mapped to Purview controls throughout the day. You leave with a control-to-obligation matrix appropriate for board and regulator briefings.'
      },
      {
        question: 'What licensing do we need to deploy Microsoft Purview after the course?',
        answer: 'Most Purview capabilities require Microsoft 365 E5 or the Compliance add-on. We cover licensing economics in Module 2 so the business case reflects realistic total cost of ownership. You leave with the licensing model and budget guidance needed for executive approval.'
      },
      {
        question: 'Can we run this as a private boardroom session for our executive team?',
        answer: 'Yes. The most common delivery model is private on-site or private virtual for up to 12 participants from a single organisation. This allows the ROI model, vendor comparison, and business case to use your actual data and constraints. Contact us for a private cohort quote.'
      },
      {
        question: 'Is there a condensed half-day option for very senior audiences?',
        answer: 'Yes — a four-hour executive condensed format is available on request. It focuses on portfolio overview, ROI headline numbers, and the business case narrative. Best suited to boards and audit committees who already have a CISO or CPO sponsoring the work.'
      },
      {
        question: 'How does this prepare us for the rest of the Purview Training Track?',
        answer: 'This is Module 1 of 6. After this course, practitioners are typically sent to Module 2 (Purview for Data Protection Practitioners), Module 5 (Purview for AI Governance), or Module 6 (Purview Enterprise Architecture) depending on the deployment scope you approve.'
      },
      {
        question: 'Is the Certificate of Completion suitable for director CPD claims?',
        answer: 'Yes. The certificate is structured for CPD claims by directors, compliance professionals (GRCI, IAPP), and senior risk officers. Eight hours of structured executive education aligned to a recognised technology and regulatory domain.'
      },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. The ROI model and business case are built up across the sessions using your own organisational data. Contact us to arrange a schedule.'
      }
    ],

    australianContext: true,
    complianceFrameworks: ['Privacy Act 1988', 'OAIC APP 11', 'Notifiable Data Breaches scheme', 'APRA CPS 234']
};
